/**
 * **********************************************************************************************************
 * V2.0
 *
 * User: firass
 * Date: 08/01/2013
 * Visual PDF: https://docs.google.com/a/liveperson.com/file/d/0B5D4lDHgXzP1NTFsTzFkR1dYVEU/edit
 *
 * This widget is used to implement a date picker on the page.
 * Options:
 *   el - mandatory. jQuery element. The element on the page where the date picker will be created.
 *   dateFormat - optional. string. Default value is ISO8601. DatePicker.DateFormat for more formats.
 *   onSelectDate - optional. Callback function of selecting date, NULL value means do nothing.
 * Example:
 * var DatePicker = require('ui/datepicker/DatePicker');
 * var datePicker = new DatePicker({
 *     el : this.ui.datePickerContainer,
 *     dateFormat : DatePicker.DateFormat.Full
 * });
 * datePicker.on(DatePicker.TRIGGERING.SELECTDATE, function (date){
 *     alert(date);
 * });
 * datePicker.render();
 *
 * *******************************************************************************************************
 */
define(/** @lends DatePicker */ function (require) {

    "use strict";

    var Translator = require("i18n/translator");
    var _ = require("underscore");
    var Marionette = require("marionette");
    var $datepicker = require("jqueryui/datepicker");
    var template = require('ui/datepicker/templates/datepicker.tmpl');
    var Bubble = require("ui/bubble/Bubble");

    /**
     * Creates a new instance of the DatePicker class.
     *
     * @constructor DatePicker
     *
     * @param {DatePicker.Options} options
     *
     * @requires marionette
     * @requires jQueryUI DatePicker
     *
     * @augments Marionette.ItemView
     *
     */
    var DatePicker = Marionette.ItemView.extend({
        ui: {
            datePicker: ".date-picker-item"
        },

        events: {
            "mouseenter .date-picker-item": "_onPickerMouseEnter",
            "mouseleave .date-picker-item": "_onPickerMouseLeave"
        },

        template: template,
        errorMsg:null,
        withUIComponents:true,

        initialize: function (options) {
            // make sure options is a valid object
            if (!options || !_.isPlainObject(options)) {
                throw "invalid argument: options is illegal.";
            }
            this.dateFormat = options.dateFormat || DatePicker.DateFormat.ISO8601;
            this.selectedDate = options.selectedDate;
            this.minDate = options.minDate;
            this.maxDate = options.maxDate;
            this.enabled = _.isUndefined(options.enabled) ? true : options.enabled;
            this.errorPosition = options.errorPosition || DatePicker.ERROR_POSITION.RIGHT;
        },

        onRender: function () {
            this.ui.datePicker.datepicker({
                dateFormat: this.dateFormat,
                onSelect: _.bind(this._triggerChange, this),
                defaultDate : this.selectedDate,
                prevText:Translator.translate("LEUIComponents.global.prev"),
                nextText:Translator.translate("LEUIComponents.global.next"),
                monthNames: this._getFullMonthsArray(),
                dayNames: this._getFullDaysArray(),
                dayNamesMin: this._getShortDaysArray()
            });
            this.setMinDate(this.minDate);
            this.setMaxDate(this.maxDate);
            this.setEnabled(this.enabled);
        },

        _getFullMonthsArray: function() {
            var monthIndexArr = _.range(1, 13);
            var arr = _.map(monthIndexArr, function(num) {
                return Translator.translate("LEUIComponents.datePickerComponent.months."+num+".full");
            });
            return arr;
        },

        _getFullDaysArray: function() {
            var dayIndexArr = _.range(1, 8);
            var arr = _.map(dayIndexArr, function(num) {
                return Translator.translate("LEUIComponents.datePickerComponent.days."+num+".full");
            });
            return arr;
        },

        _getShortDaysArray: function() {
            var dayIndexArr = _.range(1, 8);
            var arr = _.map(dayIndexArr, function(num) {
                return Translator.translate("LEUIComponents.datePickerComponent.days."+num+".short");
            });
            return arr;
        },

        /**
         * Triggering the onSelect change
         * @param date
         */
        _triggerChange: function (date) {
            this.setError(false);
            this.trigger(DatePicker.TRIGGERING.DATE_SELECTED, date);
        },

        /**
         * Gets the date picker instance.
         * @returns {datePicker instance}
         */
        _getDatePicker: function () {
            return this.ui.datePicker;
        },

        /**
         * Sets the date format for the date picker.
         * @param dateFormat
         */
        setDateFormat: function (dateFormat) {
            this.dateFormat = dateFormat;
            this._getDatePicker().datepicker("option", "dateFormat", dateFormat);
        },

        setMinDate: function(minDate){
             this.ui.datePicker.datepicker("option", "minDate", minDate);
        },

        setMaxDate: function(maxDate){
            this.ui.datePicker.datepicker("option", "maxDate", maxDate);
        },

        /**
         * Sets the selected date.
         * @param date
         */
        setSelectedDate: function (date) {
            this.ui.datePicker.datepicker('setDate', date);
        },

        /**
         * Gets the selected date.
         * @returns Date
         */
        getSelectedDate: function () {
            return  this._getDatePicker().datepicker('getDate');
        },

        /**
         * Set element enabled
         * @param enabled
         */
        setEnabled: function (enabled) {
            this.disabled = !enabled;
            if (enabled) {
                this.ui.datePicker.removeClass("disabled");
            } else {
                this.ui.datePicker.addClass("disabled");
            }
        },

        /**
         * Checks if DatePicker is disabled.
         * @returns {boolean} is disabled
         */
        isDisabled: function () {
            return this.disabled;
        },

        _onPickerMouseEnter: function() {
            if (this.ui.datePicker.hasClass("error") && this.errorMsg) {
                this._onPickerMouseLeave(null, true);
                this.uiComponents.host$.createAndRender("DatePickerError", {
                    type: Bubble,
                    options:{
                        targetControl:this.ui.datePicker,
                        text:this.errorMsg,
                        position:this.errorPosition,
                        icon:Bubble.ICON.ERROR,
                        showCloseButton:false,
                        preventCloseOnBubbleMouseEnter: false,
                        delay: 0
                    }
                });
            }
        },

        _onPickerMouseLeave: function(event, force) {
            if (this.uiComponents.DatePickerError) {
                this.uiComponents.DatePickerError.close(force);
            }
        },

        /**
         * Set error on the picker
         * @param isError
         * @param errorMsg
         */
        setError:function (isError, errorMsg) {
            if (isError) {
                this.ui.datePicker.addClass("error");
                this.errorMsg = errorMsg;
            }
            else {
                if (typeof this.ui.datePicker === "object" && "removeClass" in this.ui.datePicker) { // TODO (Gal) : There is a case (In entering personView) that we arrive to here when the dropdown is not rendered so this.ui.button is ".dropdown button" and so we do not have removeClass method
                    this.ui.datePicker.removeClass("error");
                    this.errorMsg = null;
                    if (this.uiComponents.DatePickerError) {
                        this.uiComponents.DatePickerError.close();
                    }
                }
            }
        },

        /**
         * Removes the date-picker element from the DOM, triggered when the view is close
         */
        onClose: function () {
            this._getDatePicker().datepicker("destroy");
        }

    });

    DatePicker.ERROR_POSITION = Bubble.POSITION;

    /**
     * The available date formats of date-picker.
     *
     * @enum
     * @readonly
     */
    DatePicker.DateFormat = {
        Default: "mm/dd/yy",
        ISO8601: "yy-mm-dd",
        Short: "d M, y",
        Medium: "d MM, y",
        Full: "DD, d MM, yy"
    };

    /**
     * The available triggering for date-picker.
     *
     * @enum
     * @readonly
     */
    DatePicker.TRIGGERING = {
        DATE_SELECTED: "dateselected"
    };

// return the date-picker component.
    return DatePicker;
});


